import { type MeResponse, NOC_STAGES, type NocModule, nocModuleOpensAt } from "@seagames/contracts";
import type { LinkProps } from "@tanstack/react-router";
import { sectionsFor } from "../../components/sidebar/nav";

/**
 * M7c NOC stages, GMS side. The stage and its open modules come from
 * `/auth/me.noc_stage` (null for every role but ORGANIZATION); nothing here
 * decides a stage — the API answers 403 STAGE_LOCKED regardless. This only
 * names which module a page belongs to, so a locked page can draw the
 * "not open yet" screen instead of mounting (and firing its requests).
 */

type Session = Pick<MeResponse, "active_role" | "group" | "noc_stage">;

/**
 * Path prefix → module, longest prefix wins. Paths not listed fall back to
 * `dashboard` (Sector 1 since 0063, with "/" itself). Only the page: its API routes stay staged by
 * the server whatever this says.
 * `null` = never staged (a login's own profile).
 */
const PATH_MODULES: ReadonlyArray<readonly [prefix: string, module: NocModule | null]> = [
	// Each sector's Dashboard opens with its sector (lib/dashboard/sectors.ts); Sector 4's is /contingent.
	["/dashboard/entries", "dashboard"],
	["/dashboard/accreditation", "registration"],
	["/dashboard/logistics", "travel"],
	["/accreditation/entries", "entries"],
	["/accreditation/records", "registration"],
	["/accreditation/add", "registration"],
	["/accreditation/shortlist", "shortlist"],
	["/accreditation/e-accreditation", "e_accreditation"],
	["/logistics/arrival-departure", "travel"],
	["/logistics/transportation", "transport"],
	["/logistics/food-beverages", "fnb"],
	["/accommodation/helpdesk", "helpdesk"],
	["/incident-helpdesk", "helpdesk"],
	["/accommodation", "accommodation"],
	["/contingent", "contingent"],
	// Central Report Engine: staged at Sector 4 (though the menu draws it under the Dashboard); the API stages /reports the same.
	["/reports", "contingent"],
	["/profile", null],
	["/change-password", null],
];

const under = (pathname: string, prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);

/** The module a GMS path belongs to; null when the page is never staged. */
export function nocModuleForPath(pathname: string): NocModule | null {
	const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
	let best: { len: number; module: NocModule | null } | null = null;
	for (const [prefix, module] of PATH_MODULES) {
		if (under(path, prefix) && (!best || prefix.length > best.len)) best = { len: prefix.length, module };
	}
	return best ? best.module : "dashboard";
}

/** The page each module opens on — the locked screen's way forward and a NOC's landing page. */
export const NOC_MODULE_HOME: Record<NocModule, NonNullable<LinkProps["to"]>> = {
	entries: "/accreditation/entries/eoi",
	registration: "/accreditation/records",
	travel: "/logistics/arrival-departure",
	transport: "/logistics/transportation",
	accommodation: "/accommodation",
	fnb: "/logistics/food-beverages",
	shortlist: "/accreditation/shortlist",
	e_accreditation: "/accreditation/e-accreditation",
	helpdesk: "/accommodation/helpdesk",
	dashboard: "/dashboard/entries",
	contingent: "/contingent",
};

export interface StageLock {
	module: NocModule;
	opensAtStage: number;
	stage: number;
}

/** Whether `module` is open for this session. Every role but ORGANIZATION: always. */
export function nocModuleOpenFor(session: Session, module: NocModule): boolean {
	const ns = session.active_role === "ORGANIZATION" ? session.noc_stage : null;
	return !ns || ns.open_modules.includes(module);
}

/** The lock on `pathname` for this session, or null when the page may render. */
export function stageLockFor(session: Session, pathname: string): StageLock | null {
	if (session.active_role !== "ORGANIZATION" || !session.noc_stage) return null;
	const module = nocModuleForPath(pathname);
	if (module === null || nocModuleOpenFor(session, module)) return null;
	return { module, opensAtStage: nocModuleOpensAt(module), stage: session.noc_stage.stage };
}

/** Every page the session's T menu (the primary sidebar) offers, as paths. */
function offeredPaths(session: Session): Set<string> {
	return new Set(
		sectionsFor(session).flatMap((s) => s.items.flatMap((i) => (i.kind === "leaf" ? [String(i.to)] : i.children.map((c) => String(c.to))))),
	);
}

/**
 * The locked screen's way forward: the first page of the NEWEST stage the
 * NOC has reached that its menu offers (View Records at stage 2), else an
 * earlier stage's (EOI). Null when no open page is reachable.
 */
export function nocLatestPath(session: Session): NonNullable<LinkProps["to"]> | null {
	if (session.active_role !== "ORGANIZATION" || !session.noc_stage) return null;
	const offered = offeredPaths(session);
	for (const stage of [...NOC_STAGES].reverse()) {
		for (const module of stage.modules) {
			const to = NOC_MODULE_HOME[module];
			if (session.noc_stage.open_modules.includes(module) && offered.has(String(to))) return to;
		}
	}
	return null;
}

/**
 * Where a staged NOC starts: the first open module, in stage order, whose
 * page its menu actually offers (EOI needs `competition.declare` — sending a
 * login without it there would bounce back to "/" forever). Null when no
 * open page is reachable; the dashboard's locked screen then explains.
 */
export function nocHomePath(session: Session): NonNullable<LinkProps["to"]> | null {
	if (session.active_role !== "ORGANIZATION" || !session.noc_stage) return null;
	const offered = offeredPaths(session);
	for (const stage of NOC_STAGES) {
		for (const module of stage.modules) {
			const to = NOC_MODULE_HOME[module];
			if (session.noc_stage.open_modules.includes(module) && offered.has(String(to))) return to;
		}
	}
	return null;
}
